import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'heroGhost' | 'heroDanger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Icon to display before the text */
  icon?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Children content */
  children: React.ReactNode;
}

/**
 * Button component from AgroBook design system
 *
 * Primary actions use the green-800 background
 * Secondary actions have white background with green border
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  fullWidth = false,
  children,
  className = '',
  ...props
}) => {
  const baseStyles = 'inline-flex items-center gap-2 rounded-sm font-body font-semibold transition-agrobook duration-agrobook whitespace-nowrap tracking-wide disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: 'bg-green-800 text-white hover:bg-green-700 hover:shadow-sm',
    secondary: 'bg-white text-green-800 border-thick border-green-400 hover:bg-green-100',
    ghost: 'bg-transparent text-green-700 hover:bg-green-100',
    danger: 'bg-red-50 text-red-600 border-thick border-red-200 hover:bg-red-100',
    heroGhost: 'bg-white/[.18] text-white border border-white/35 hover:bg-white/25',
    heroDanger: 'bg-red-600/30 text-white border border-red-400/50 hover:bg-red-600/40',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-3 text-button',
    lg: 'px-6 py-3 text-base',
  };

  const widthStyle = fullWidth ? 'w-full justify-center' : '';

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${className}`}
      {...props}
    >
      {icon && <span className="flex items-center">{icon}</span>}
      {children}
    </button>
  );
};
